/* Landing enhancements: no catalog, builder or cart state. Motion is scroll-linked,
   respects reduced motion, stops offscreen, and is cleaned up on navigation. */
(() => {
  'use strict';
  let dispose = () => {};
  const clamp = value => Math.max(0, Math.min(1, value));
  function mount() {
    dispose();
    const story = document.querySelector('[data-ring-story]');
    const footer = document.querySelector('.site-footer');
    if (!story && !footer) { dispose = () => {}; return; }
    const controller = new AbortController(), {signal} = controller;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let disposed = false, frame = 0, animated = false;
    let storyVisible = true, footerVisible = true;
    function draw() {
      frame = 0;
      if (disposed || document.hidden) return;
      if (story && (storyVisible || !animated)) {
        let progress = 1;
        if (animated) {
          const top = Number(story.dataset.header) || 0;
          const rect = story.getBoundingClientRect();
          progress = clamp((top - rect.top) / Math.max(1, rect.height - (innerHeight - top)));
        }
        const t = clamp((progress - .06) / .76);
        const eased = t * t * (3 - 2 * t);
        // Old Miner artwork has a taller crown; seat its culet within the basket.
        story.style.setProperty('--ring-drop', (18.5 * eased).toFixed(3) + '%');
        story.style.setProperty('--ring-lift', (-7 * eased).toFixed(3) + '%');
        story.style.setProperty('--ring-progress', eased.toFixed(3));
        // Move the whole setting too: a small lift, turn and approach to camera.
        story.style.setProperty('--ring-turn', (-12 * (1 - eased)).toFixed(3) + 'deg');
        story.style.setProperty('--ring-drift', (18 * (1 - eased)).toFixed(3) + 'px');
        story.style.setProperty('--ring-scale', (.9 + .1 * eased).toFixed(3));
        // A restrained flash as the diamond seats; no perpetual sparkle loop.
        story.style.setProperty('--ring-sheen', reduced.matches ? '0' : Math.pow(Math.sin(eased * Math.PI), 6).toFixed(3));
      }
      if (footer && footerVisible) {
        const rect = footer.getBoundingClientRect();
        const progress = reduced.matches ? 1 : clamp((innerHeight - rect.top) / Math.min(innerHeight, rect.height));
        footer.style.setProperty('--skyline-rise', (18 * (1 - progress)).toFixed(2) + 'px');
        footer.style.setProperty('--skyline-glow', (.06 + progress * .14).toFixed(3));
        footer.style.setProperty('--skyline-light', (15 + progress * 70).toFixed(2) + '%');
      }
    }
    function schedule() { if (!frame && !disposed && !document.hidden) frame = requestAnimationFrame(draw); }
    function configure() {
      if (story) {
        const top = Math.ceil(document.querySelector('.header')?.getBoundingClientRect().height || 100);
        animated = !reduced.matches && innerHeight - top >= (innerWidth <= 650 ? 575 : 480);
        story.dataset.header = String(top);
        story.style.setProperty('--ring-header', top + 'px');
        story.classList.toggle('is-scroll-ready', animated);
      }
      schedule();
    }
    window.addEventListener('scroll', schedule, {passive:true,signal});
    window.addEventListener('resize', configure, {passive:true,signal});
    reduced.addEventListener('change', configure, {signal});
    document.addEventListener('visibilitychange', schedule, {signal});
    let observer;
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.target === story) storyVisible = entry.isIntersecting;
          if (entry.target === footer) footerVisible = entry.isIntersecting;
        }
        schedule();
      });
      if (story) observer.observe(story);
      if (footer) observer.observe(footer);
    }
    configure();
    dispose = () => { disposed = true; controller.abort(); observer?.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }
  window.addEventListener('hashchange', mount);
  window.addEventListener('pagehide', () => dispose());
  window.addEventListener('pageshow', event => { if (event.persisted) mount(); });
  mount();
})();

/* Decorative, scroll-linked assembly inside the existing bridal copy panel. */
(() => {
  'use strict';
  const FULL_RING_URL = "https://cdn.shopify.com/s/files/1/0638/7836/5339/files/tjs-marquise-white-gold-six-prong.png?v=1791591510&width=1000";
  const SETTING_URL = "https://cdn.shopify.com/s/files/1/0638/7836/5339/files/tjs-marquise-white-gold-setting.png?v=1791591509&width=1000";
  const STONE_URL = 'https://cdn.shopify.com/s/files/1/0638/7836/5339/files/tjs-marquise-loose-diamond-clean-20261010.png?v=1791594091&width=1000';
  const OWNER = '__TJSBridalRingAccent';
  const SELECTOR = '.home-bridal .home-bridal-copy';
  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = value => value * value * (3 - 2 * value);
  window[OWNER]?.destroy();

  const lifecycle = new AbortController();
  let root = null, observer = null, active = null, sleeping = false;

  function mount(copy) {
    const controller = new AbortController(), {signal} = controller;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const accent = document.createElement('div');
    accent.className = 'tjs-bridal-accent';
    accent.dataset.bridalRingAccent = '';
    accent.innerHTML = '<div class="tjs-bridal-accent-art" aria-hidden="true">' +
      '<img class="tjs-bridal-accent-setting" alt="Engagement ring setting" width="1448" height="1086" loading="lazy" decoding="async">' +
      '<img class="tjs-bridal-accent-stone" alt="Lab-grown diamond center stone" width="1448" height="1086" loading="lazy" decoding="async">' +
      '<img class="tjs-bridal-accent-finished" alt="Finished lab-grown diamond engagement ring" width="1448" height="1086" loading="lazy" decoding="async">' +
      '</div><a class="tjs-bridal-accent-link" href="#/collection/engagement">Make it yours<span aria-hidden="true"> ↗</span></a>';
    const setting = accent.querySelector('.tjs-bridal-accent-setting');
    const stone = accent.querySelector('.tjs-bridal-accent-stone');
    const finished = accent.querySelector('.tjs-bridal-accent-finished');
    const link = accent.querySelector('.tjs-bridal-accent-link');
    let frame = 0, disposed = false, visible = false, settingFailed = false;
    let fullFailed = false, stoneFailed = false, intersectionObserver;
    let lastProgress = -1;

    function cancelFrame() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    }
    function loaded(image) { return image.complete && image.naturalWidth > 0; }
    function progress() {
      if (reduced.matches || settingFailed || stoneFailed || !loaded(finished) || !loaded(setting) || !loaded(stone)) return 1;
      const height = window.innerHeight || document.documentElement.clientHeight;
      const headerHeight = document.querySelector('.header')?.getBoundingClientRect().height || 0;
      const start = height * .85;
      const end = Math.min(start - 1, Math.max(height * .30, headerHeight + 24));
      return clamp((start - copy.getBoundingClientRect().top) / (start - end));
    }
    function paint(value) {
      if (value === lastProgress) return;
      lastProgress = value;
      const assembled = ease(value);
      // In the final tenth, the untouched photograph takes over all six prongs.
      const finish = ease(clamp((value - .90) / .10));
      const caption = ease(clamp((value - .84) / .16));
      const partsOpacity = (1 - finish).toFixed(3);
      setting.style.transform = 'translate3d(' + ((1 - assembled) * 12).toFixed(3) + '%,0,0)';
      stone.style.transform = 'translate3d(' + ((assembled - 1) * 38).toFixed(3) + '%,0,0)';
      setting.style.opacity = partsOpacity;
      stone.style.opacity = partsOpacity;
      finished.style.opacity = finish.toFixed(3);
      link.style.opacity = caption.toFixed(3);
      link.style.transform = 'translate3d(0,' + ((1 - caption) * 5).toFixed(2) + 'px,0)';
      // An unrevealed decorative invitation must not add an invisible tab stop.
      link.tabIndex = caption >= .5 ? 0 : -1;
      link.style.pointerEvents = caption >= .5 ? '' : 'none';
    }
    function draw() {
      frame = 0;
      if (disposed || fullFailed || document.hidden || !visible) return;
      paint(progress());
    }
    function schedule() {
      if (!frame && !disposed && !fullFailed && !document.hidden && visible) {
        frame = requestAnimationFrame(draw);
      }
    }
    function updateVisibility() {
      const bounds = copy.getBoundingClientRect();
      visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
      if (visible) schedule(); else cancelFrame();
    }
    function assetReady() {
      if (disposed || fullFailed) return;
      lastProgress = -1;
      // Initialize before it enters the viewport as lazy images become ready.
      paint(progress());
    }
    function configureMotion() {
      cancelFrame();
      lastProgress = -1;
      paint(progress());
      schedule();
    }

    finished.addEventListener('load', assetReady, {signal});
    setting.addEventListener('load', assetReady, {signal});
    stone.addEventListener('load', assetReady, {signal});
    stone.addEventListener('error', () => { stoneFailed = true; assetReady(); }, {signal});
    setting.addEventListener('error', () => { settingFailed = true; assetReady(); }, {signal});
    finished.addEventListener('error', () => {
      fullFailed = true;
      cancelFrame();
      accent.hidden = true;
      copy.classList.remove('has-tjs-bridal-accent');
    }, {signal});
    setting.src = SETTING_URL;
    stone.src = STONE_URL;
    finished.src = FULL_RING_URL;
    copy.classList.add('has-tjs-bridal-accent');
    copy.prepend(accent);
    paint(progress());
    updateVisibility();

    if (typeof IntersectionObserver !== 'undefined') {
      intersectionObserver = new IntersectionObserver(entries => {
        if (disposed) return;
        for (const entry of entries) if (entry.target === copy) visible = entry.isIntersecting;
        if (visible) schedule(); else cancelFrame();
      });
      intersectionObserver.observe(copy);
    }
    window.addEventListener('scroll', () => {
      if (!intersectionObserver) updateVisibility();
      schedule();
    }, {passive: true, signal});
    window.addEventListener('resize', updateVisibility, {passive: true, signal});
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) cancelFrame(); else updateVisibility();
    }, {signal});
    reduced.addEventListener('change', configureMotion, {signal});
    link.addEventListener('focus', () => {
      link.style.opacity = '1';
      link.style.transform = 'none';
    }, {signal});

    return {
      copy, accent,
      dispose() {
        disposed = true;
        controller.abort();
        intersectionObserver?.disconnect();
        cancelFrame();
        accent.remove();
        copy.classList.remove('has-tjs-bridal-accent');
      }
    };
  }

  function reconcile() {
    if (sleeping) return;
    const copy = root?.querySelector(SELECTOR);
    if (active && active.copy === copy && active.accent.isConnected) return;
    active?.dispose();
    active = null;
    if (copy) active = mount(copy);
  }
  function start() {
    sleeping = false;
    const main = document.querySelector('#main') || document.querySelector('main');
    if (main !== root || !observer) {
      observer?.disconnect();
      root = main;
      if (root && typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(reconcile);
        // Attribute updates from animation cannot retrigger this observer.
        observer.observe(root, {childList: true, subtree: true});
      }
    }
    reconcile();
  }
  function stop() {
    sleeping = true;
    observer?.disconnect();
    observer = null;
    active?.dispose();
    active = null;
  }
  function navigate() {
    active?.dispose();
    active = null;
    start();
  }
  window.addEventListener('hashchange', navigate, {signal: lifecycle.signal});
  window.addEventListener('pagehide', stop, {signal: lifecycle.signal});
  window.addEventListener('pageshow', event => { if (event.persisted) start(); }, {signal: lifecycle.signal});
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {once: true, signal: lifecycle.signal});
  } else start();
  window[OWNER] = {destroy() { stop(); lifecycle.abort(); }};
})();
